// External libraries
import { Component, createRef } from "react";

// FontAwesome icons
import { faPenLine, faTrashCan } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Preact Signals
import { effect } from "@preact/signals-react";

// Kendo React components
import { Button } from "@progress/kendo-react-buttons";
import { FieldWrapper } from "@progress/kendo-react-form";
import { TextArea } from "@progress/kendo-react-inputs";
import { Error, Hint, Label } from "@progress/kendo-react-labels";
import { Ripple } from "@progress/kendo-react-ripple";

// Internal components
import { If } from "../../Common/UI/IfComponent";
import { TooltipPopupIcon } from "../../Common/UI/TooltipPopupIcon";

// Utilities
import { Invisible } from "../../Utilities/EntryUtils";

// Signals
import { userData } from "../../Common/Store/CommonSignals";
import {
  disabledSections,
  formErrors,
  initialCommentsLengths,
  template,
} from "../../Entry/Manage/EditEntrySignals";

export class ContinuousCommentsField extends Component {
  inputRef = createRef();

  constructor({
    field,
    value,
    handleFieldChange,
    sectionIndex,
    fieldIndex,
    eventIndex,
    labelVisible,
    isPrintPreview,
  }) {
    super();
    const { fieldName, properties } = field;
    const ACCEPTED_TYPES = ["object"];
    const comments = ACCEPTED_TYPES.includes(typeof JSON.tryParse(value, []))
      ? JSON.tryParse(value, [])
      : [];
    const temporaryComment = comments?.find((c) => c.isTemporary);
    this.originalCommentsLength = Array.isArray(comments) ? comments.length : 0;
    this.storeKey = `${fieldName}`;

    if (initialCommentsLengths.value[this.storeKey] === undefined) {
      initialCommentsLengths.value = {
        ...initialCommentsLengths.value,
        [this.storeKey]: this.originalCommentsLength,
      };
    }

    this.state = {
      comment: temporaryComment ? temporaryComment.comment : "",
      confirmedComments: comments,
      touched: false,
      valid: this.validate(
        comments,
        temporaryComment ? temporaryComment.comment : "",
        properties?.IsRequired
      ),
      invisible: false,
      disabled: false,
      editingIndex: null,
      editingText: "",
    };
    this.field = field;
    this.fieldName = typeof eventIndex === "number" ? `${fieldName}_${eventIndex}` : fieldName;
    this.hint = properties?.Hint;
    this.fieldInformation = properties?.FieldInformation;
    this.maxLength = properties?.MaxLength;
    this.isRequired = properties?.IsRequired;
    this.isReadOnly = properties?.IsReadOnly;
    this.displayNameHorizontally = properties?.DisplayNameHorizontally;
    this.enableCommentActions = properties?.EnableCommentActions;
    this.visibilityRules = properties?.VisibilityRules;
    this.validationMessage = "Type a comment.";
    this.label = properties?.Label || `${fieldName}:`;
    this.labelVisible = labelVisible;
    this.isPrintPreview = isPrintPreview;
    this.sectionIndex = sectionIndex;
    this.fieldIndex = fieldIndex;
    this.eventIndex = eventIndex;
    this.sectionName = template.value?.sections?.[sectionIndex]?.sectionName;
    this.handleFieldChange = handleFieldChange;
    this.disposeDependencyEffect = () => {};
    this.disposeValidationEffect = () => {};
    this.disposeDisabledSectionsEffect = () => {};
  }

  componentDidMount() {
    if (this.isRequired) {
      if (
        !this.validate(this.state.confirmedComments, this.state.comment, this.isRequired) &&
        !formErrors.value[this.sectionName]?.includes(this.fieldName)
      ) {
        const newFormErrors = { ...formErrors.value };
        newFormErrors[this.sectionName] = newFormErrors[this.sectionName] || [];
        newFormErrors[this.sectionName].push(this.fieldName);
        formErrors.value = newFormErrors;
      }
      this.handleValidationEffect();
      this.updateFormErrors();
    }
    if (this.visibilityRules) this.handleDependencyEffect();

    this.disposeDisabledSectionsEffect = effect(() => {
      const isSectionDisabled = disabledSections.value[this.sectionName];
      if (this.state.disabled !== isSectionDisabled) {
        this.setState({ disabled: isSectionDisabled });
      }
    });
  }

  componentWillUnmount() {
    this.disposeValidationEffect();
    this.disposeDependencyEffect();
    this.disposeDisabledSectionsEffect();
  }

  shouldComponentUpdate(_, nextState) {
    return (
      this.state.comment !== nextState.comment ||
      this.state.confirmedComments !== nextState.confirmedComments ||
      this.state.valid !== nextState.valid ||
      this.state.invisible !== nextState.invisible ||
      this.state.disabled !== nextState.disabled ||
      this.state.touched !== nextState.touched ||
      this.state.editingIndex !== nextState.editingIndex ||
      this.state.editingText !== nextState.editingText
    );
  }

  getSnapshotBeforeUpdate(_, prevState) {
    return {
      visibilityChange: prevState.invisible !== this.state.invisible,
      valueChange: prevState.confirmedComments !== this.state.confirmedComments,
    };
  }

  componentDidUpdate(_, __, snapshot) {
    if (snapshot?.visibilityChange) {
      if (this.state.invisible) {
        this.clearFormErrors();
      }
    }

    if (!this.state.invisible && this.isRequired) {
      this.setState(
        (prevState) => ({
          valid: this.validate(prevState.confirmedComments, prevState.comment, this.isRequired),
        }),
        () => {
          this.updateFormErrors();
        }
      );
    }
    if (this.conditionalFormattingRules && snapshot?.valueChange) this.handleStyleEffect();
    if (this.state.disabled) this.clearFormErrors();
  }

  handleDependencyEffect() {
    this.disposeDependencyEffect = () => {};
    this.disposeDependencyEffect = effect(() => {
      const result = Invisible({
        VisibilityRules: this.visibilityRules,
        parameterValues: {},
        eventIndex: this.eventIndex,
        galleryName: this.galleryParentName,
      });

      const isNowInvisible = result.invisible && !result.disabled;
      const isNowDisabled = result.invisible && result.disabled;

      // Prepare the new state object
      const newState = {
        invisible: isNowInvisible,
        disabled: isNowDisabled,
      };

      // If not invisible and field is required, validate
      if (!isNowInvisible && this.isRequired) {
        const isValid = this.validate(
          this.state.confirmedComments,
          this.state.comment,
          this.isRequired
        );
        newState.valid = isValid;
      }

      // Single setState call with all updates
      this.setState(newState, () => {
        if (isNowInvisible) {
          this.clearFormErrors();
        } else if (this.isRequired && !newState.valid) {
          // Handle form errors
          if (!formErrors.value[this.sectionName]?.includes(this.fieldName)) {
            const newFormErrors = { ...formErrors.value };
            newFormErrors[this.sectionName] = newFormErrors[this.sectionName] || [];
            if (!newFormErrors[this.sectionName].includes(this.fieldName)) {
              newFormErrors[this.sectionName].push(this.fieldName);
              formErrors.value = newFormErrors;
            }
          }
        }
      });
    });
  }

  handleValidationEffect() {
    this.disposeValidationEffect = effect(() => {
      this.setState({
        valid: formErrors.value[this.sectionName]
          ? formErrors.value[this.sectionName]?.indexOf(this.fieldName) === -1
          : true,
      });
    });
  }

  validate(confirmedValues, currentValue, isRequired) {
    if (!isRequired) return true;
    const originalLength = initialCommentsLengths?.value[this.storeKey] || 0;
    return (
      (Array.isArray(confirmedValues) && confirmedValues?.length > originalLength) ||
      currentValue?.length > 0
    );
  }

  updateFormErrors() {
    if (!this.state.valid) {
      if (
        formErrors.value[this.sectionName]?.indexOf(this.fieldName) === -1 ||
        !formErrors.value[this.sectionName]
      ) {
        const newFormErrors = { ...formErrors.value };
        newFormErrors[this.sectionName] = newFormErrors[this.sectionName] || [];
        newFormErrors[this.sectionName].push(this.fieldName);
        formErrors.value = newFormErrors;
      }
    } else {
      this.clearFormErrors();
    }
  }

  clearFormErrors() {
    const newFormErrors = { ...formErrors.value };
    const sectionErrors = newFormErrors[this.sectionName];
    if (sectionErrors) {
      newFormErrors[this.sectionName] = sectionErrors.filter((error) => error !== this.fieldName);
    }
    formErrors.value = newFormErrors;
  }

  formatDateTime = () => {
    const date = new Date();
    const offset = date.getTimezoneOffset() * 60000;
    const isoDate = new Date(date.getTime() - offset).toISOString().replace("Z", "");

    return isoDate;
  };

  formatDateTimeForDisplay = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12 || 12;

    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const year = date.getFullYear();

    return `${month}/${day}/${year}, ${hours}:${minutes} ${ampm}`;
  };

  breakLines = (text, maxLen = 45) => {
    if (!text) return "";
    const regex = new RegExp(`.{1,${maxLen}}`, "g");
    return text.match(regex).join("\n");
  };

  getCurrentUsername = () => {
    const email = userData.value.userEmail || "";
    return email.split("@")[0];
  };

  handleAddComment = () => {
    const { comment, confirmedComments, editingIndex, editingText } = this.state;
    if (editingIndex !== null) {
      const updatedComments = confirmedComments.map((c, i) =>
        i === editingIndex ? { ...c, comment: editingText, date: this.formatDateTime() } : c
      );
      this.setState(
        {
          confirmedComments: updatedComments,
          editingIndex: null,
          editingText: "",
          comment: "",
          touched: true,
          valid: this.validate(updatedComments, "", this.isRequired),
        },
        () => {
          this.updateFormErrors();
        }
      );

      this.handleFieldChange({
        e: { target: { name: this.field.fieldName }, value: JSON.stringify(updatedComments) },
        sectionIndex: this.sectionIndex,
        fieldIndex: this.fieldIndex,
        eventIndex: this.eventIndex,
      });
    } else if (comment.trim() !== "") {
      const name = this.getCurrentUsername();
      const formattedDate = this.formatDateTime();
      const newCommentObj = { name, date: formattedDate, comment };
      const newComments = [newCommentObj, ...confirmedComments];

      this.setState(
        {
          confirmedComments: newComments,
          comment: "",
          touched: true,
          valid: this.validate(newComments, "", this.isRequired),
        },
        () => {
          this.updateFormErrors();
        }
      );

      this.handleFieldChange({
        e: { target: { name: this.field.fieldName }, value: JSON.stringify(newComments) },
        sectionIndex: this.sectionIndex,
        fieldIndex: this.fieldIndex,
        eventIndex: this.eventIndex,
      });
    } else {
      this.setState({ touched: true, valid: !this.isRequired }, () => {
        this.updateFormErrors();
      });
    }
  };

  handleDeleteComment = (index) => {
    const { confirmedComments } = this.state;
    const newComments = confirmedComments.filter((_, i) => i !== index);
    this.setState(
      (prevState) => ({
        confirmedComments: newComments,
        valid: this.validate(newComments, prevState.comment, this.isRequired),
      }),
      () => {
        this.updateFormErrors();
      }
    );

    this.handleFieldChange({
      e: { target: { name: this.field.fieldName }, value: JSON.stringify(newComments) },
      sectionIndex: this.sectionIndex,
      fieldIndex: this.fieldIndex,
      eventIndex: this.eventIndex,
    });
  };

  handleEditComment = (index) => {
    const { confirmedComments } = this.state;
    this.setState({ editingIndex: index, editingText: confirmedComments[index].comment });
  };

  handleEditInputChange = (e) => {
    this.setState({ editingText: e.value });
  };

  handleTextAreaBlur = () => {
    if (this.state.editingIndex !== null || this.state.comment.trim() === "") return;
    const newCommentObj = {
      name: this.getCurrentUsername(),
      date: this.formatDateTime(),
      comment: this.state.comment,
      isTemporary: true,
    };

    const updatedComments = [
      newCommentObj,
      ...this.state.confirmedComments.filter((c) => !c.isTemporary),
    ];

    this.handleFieldChange({
      e: { target: { name: this.field.fieldName }, value: JSON.stringify(updatedComments) },
      sectionIndex: this.sectionIndex,
      fieldIndex: this.fieldIndex,
      eventIndex: this.eventIndex,
    });
  };

  render() {
    if (this.state.invisible) return null;
    if (this.isPrintPreview) return null;

    const username = this.getCurrentUsername();
    return (
      <Ripple>
        <div
          ref={this.inputRef}
          className="k-d-flex k-flex-col"
        >
          <FieldWrapper>
            {this.displayNameHorizontally ? (
              <div className="k-d-flex k-align-items-start k-mt-5">
                {this.labelVisible && (
                  <Label
                    editorId={this.field.fieldName}
                    className="comment-label"
                  >
                    {this.label}
                  </Label>
                )}
                <div
                  className="k-form-field-wrap"
                  style={{ flex: 1 }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <TextArea
                      value={
                        this.state.editingIndex !== null
                          ? this.state.editingText
                          : this.state.comment
                      }
                      onChange={(e) => {
                        if (this.state.editingIndex !== null) {
                          this.handleEditInputChange(e);
                        } else {
                          this.setState({ comment: e.value });
                        }
                      }}
                      onBlur={this.handleTextAreaBlur}
                      maxLength={this.maxLength}
                      disabled={this.isReadOnly || this.state.disabled}
                      required={this.isRequired && !this.state.valid}
                      autoSize={true}
                      placeholder="Type a comment..."
                    />
                    <If
                      condition={
                        this.fieldInformation !== undefined &&
                        this.fieldInformation !== "" &&
                        this.fieldInformation !== "<p></p>"
                      }
                    >
                      <TooltipPopupIcon content={this.fieldInformation} />
                    </If>
                  </div>
                  <div className="comment-input-container">
                    <Button
                      onClick={this.handleAddComment}
                      themeColor="primary"
                      className="comment-button"
                      disabled={
                        this.isReadOnly ||
                        this.state.disabled ||
                        (this.state.editingIndex === null && !this.state.comment.trim())
                      }
                      type="button"
                    >
                      {this.state.editingIndex !== null ? "Save" : "Add Comment"}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {this.labelVisible && (
                  <Label
                    id={this.field.fieldName + "_label"}
                    className="comment-label"
                  >
                    {this.label}
                  </Label>
                )}
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <TextArea
                    value={
                      this.state.editingIndex !== null ? this.state.editingText : this.state.comment
                    }
                    onChange={(e) => {
                      if (this.state.editingIndex !== null) {
                        this.handleEditInputChange(e);
                      } else {
                        this.setState({ comment: e.value });
                      }
                    }}
                    onBlur={this.handleTextAreaBlur}
                    maxLength={this.maxLength}
                    disabled={this.isReadOnly || this.state.disabled}
                    required={this.isRequired && !this.state.valid}
                    autoSize={true}
                    placeholder="Type a comment..."
                    className="comment-textarea"
                  />
                  <If
                    condition={
                      this.fieldInformation !== undefined &&
                      this.fieldInformation !== "" &&
                      this.fieldInformation !== "<p></p>"
                    }
                  >
                    <TooltipPopupIcon content={this.fieldInformation} />
                  </If>
                </div>
                <div className="comment-input-container">
                  <Button
                    onClick={this.handleAddComment}
                    themeColor="primary"
                    className="comment-button"
                    disabled={
                      this.isReadOnly ||
                      this.state.disabled ||
                      (this.state.editingIndex === null && !this.state.comment.trim())
                    }
                    type="button"
                  >
                    {this.state.editingIndex !== null ? "Save" : "Add Comment"}
                  </Button>
                </div>
              </>
            )}
            <If condition={this.hint}>
              <Hint>{this.hint}</Hint>
            </If>
            <If condition={!this.state.valid && this.state.touched}>
              <Error>{this.validationMessage}</Error>
            </If>
            <div className="comments-section">
              {this.state.confirmedComments.length > 0 && (
                <div className="comments-header">
                  Comments{" "}
                  <span className="comments-count">
                    ({this.state.confirmedComments.filter((c) => !c.isTemporary).length})
                  </span>
                </div>
              )}
              <ul className="comments-list">
                {this.state.confirmedComments
                  .filter((c) => !c.isTemporary)
                  .map((c, i) => (
                    <li
                      key={c.name + "_" + i}
                      className="comment-item"
                    >
                      <div className="comment-header">
                        <span className="comment-author">{c.name || "User"}</span>
                        <span className="comment-date">
                          {this.formatDateTimeForDisplay(c.date)}
                        </span>
                        {!this.isPrintPreview &&
                          this.enableCommentActions &&
                          c.name === username && (
                            <span className="comment-actions">
                              <Button
                                onClick={() => this.handleEditComment(i)}
                                className="comment-action-button"
                                themeColor="secondary"
                                type="button"
                              >
                                <FontAwesomeIcon icon={faPenLine} />
                              </Button>
                              <Button
                                onClick={() => this.handleDeleteComment(i)}
                                className="comment-action-button"
                                themeColor="error"
                                type="button"
                              >
                                <FontAwesomeIcon icon={faTrashCan} />
                              </Button>
                            </span>
                          )}
                      </div>
                      <div className="comment-content">{this.breakLines(c.comment)}</div>
                    </li>
                  ))}
              </ul>
            </div>
          </FieldWrapper>
        </div>
      </Ripple>
    );
  }
}
